/**
 * The customer account book (T651 M2) — the navigation, settled once.
 *
 * ⭐ WHAT THIS FILE IS FOR. Three later milestones each fill exactly one child
 * below, and they may be built in parallel. If the tabs, the props or the
 * invalidation callback landed with those milestones instead, two of them would
 * be editing the SAME declarations - not merely the same files - and the classic
 * outcome is a positional binding that swaps two same-shaped arrays and still
 * compiles. So the whole contract lands here, in one commit, and each later
 * milestone owns one child file and nothing above it.
 *
 * ⛔ ONE LIST AT A TIME, THREE TABS, NO NEW SHELF COVER. The price-book shelf
 * next door pins its own interactive budget at 5 fixed covers (4 services +
 * Add-ons) plus the decision cover plus a search; this book is reached by a
 * quiet entry beneath it instead. Simplicity is the hard
 * constraint on this product - the operators bounced off a prior tool that was
 * "too many buttons" - so three tabs inside one quiet entry is the budget here.
 * ⚠️ BOARD 938 SCOPE NOTE: the shelf figure quoted above is a COMPONENT count
 * and it is accurate as such. DESIGN.md §1 rule 2 is a SCREEN rule, and the
 * shelf screen measures NINE once App.tsx's Back button and the quiet entry
 * that reaches THIS book are counted. The "no new shelf cover" ruling above is
 * strengthened by that, not weakened - the screen has no room to spare. The
 * real numbers are pinned in test/client/control-budget.spec.tsx (T938 R2).
 *
 * ⚠️ THE ONE THING THIS CONTRACT DOES NOT CARRY, said here so it is not
 * discovered halfway through filling a child: the WRITE calls. Each child's
 * create/edit/deactivate action lands with the milestone that builds its server
 * route, because a client action posting to a route nobody has written yet is
 * dead code. That is an APPEND of a new named export, not an edit to anything
 * declared here or in Admin.tsx — the positional tuple, the view union and the
 * props below stay closed either way.
 *
 * ⛔ `onChanged` IS THE HOUSE INVALIDATION AND IT IS THE SAME ONE for all three
 * children: Admin.tsx passes its `load`, which refetches every list on the
 * screen. A child that wrote a row and refreshed only its own list would leave
 * the other two showing pre-change data - a price list that still names a
 * destination the operator just renamed, for instance.
 *
 * ⛔ THE TRUNCATION LINE LIVES HERE, NOT IN A CHILD, AND THAT IS WHAT KEEPS THE
 * FREEZE HOLDING (T651 M2 hardening). All four reads are bounded by a
 * server-side LIMIT and used to cut their list SILENTLY; the operator has to be
 * told. Putting the notice in the children would have meant a fifth prop on
 * three stubs that M3, M4 and M5 are each about to fill in parallel - exactly
 * the same-declaration collision this file exists to prevent. So the flags stop
 * here, the three child prop contracts are byte-for-byte what M2 froze, and the
 * notice is rendered once, for the tab actually showing a cut list.
 */
import { useState } from 'preact/hooks';
import type {
  ApiAccount,
  ApiBillingParty,
  ApiDestination,
} from '../../lib/actions.js';
import { Button } from '@ecmb/ui';
import { Banner } from '../../components/Banner.js';
import { AccountList } from './AccountList.js';
import { DestinationList } from './DestinationList.js';

/** Which of the four bounded reads came back cut short. One flag per LIST, not
 *  one for the book: a merged flag would tell the operator rows are missing from
 *  the list she is reading when it is a different list that was cut. */
export type BookTruncation = {
  accounts: boolean;
  destinations: boolean;
  prices: boolean;
  billingParties: boolean;
};

/**
 * Which of the four bounded reads FAILED. One flag per list for the same reason
 * `BookTruncation` is, and structurally identical to it on purpose — the two
 * facts are per-list for the same argument, so they are shaped the same.
 *
 * ⛔ BECAUSE THEY ARE STRUCTURALLY IDENTICAL, `tsc` CANNOT CATCH A SWAPPED
 * PROP. Four booleans is four booleans. They are passed as NAMED props below so
 * a swap is at least visible in review, and the spec that catches it uses
 * DISTINGUISHABLE fixtures — one list truncated, a DIFFERENT list failed —
 * asserting both the right notice's presence and the wrong one's absence.
 *
 * ⭐ AND `truncated` AND `failed` ARE INDEPENDENT, NOT EXCLUSIVE. `truncated.x`
 * requires read x to have SUCCEEDED; `failed.y` requires read y to have FAILED.
 * On the prices tab both can be true at once, which is why the two banners
 * below stack rather than choosing between themselves.
 */
export type BookFailure = {
  accounts: boolean;
  destinations: boolean;
  prices: boolean;
  billingParties: boolean;
};

export type AccountBookProps = {
  accounts: ApiAccount[];
  destinations: ApiDestination[];
  billingParties: ApiBillingParty[];
  truncated: BookTruncation;
  /** Which reads failed. A failed read arrives here as an EMPTY list, so
   *  without this the book cannot tell "nothing captured yet" from "could not
   *  ask" — which is the whole defect this prop exists to close. */
  failed: BookFailure;
  /** False until the FIRST load settles. `EMPTY_BOOK` is indistinguishable from
   *  a real empty book, so the sub-second before the first resolve would
   *  otherwise read as "nothing captured yet" too. */
  loaded: boolean;
  /* ⭐ board 2812 M4 fix round (L8): `prices` and `meta` LEFT with the Agreed prices tab.
     A home's prices live only on its own Prices tab now (q747's reversal, one place:
     `HomePage.tsx`'s `HomePrices`, which hands `PriceList` the same snapshot), and
     `meta` was read by that tab alone. */
  /**
   * ⭐⭐ T1749 M7 wave-3 (security L5) — `onBack` IS GONE, WITH ITS BRANCH. It was
   * added one wave earlier so the book could render without a Back control on its
   * new host, and then NO HOST PASSED IT: the Price lists shelf stopped rendering
   * the book at all (R5: it "hosts no account operations"), so the branch it
   * guarded was dead code with a docblock explaining a choice nobody could make.
   * The book is a SECTION of a rail destination, which has App's single Back
   * control and its own `h1`; it renders neither.
   */
  /**
   * ⭐ T1749 M7 wave-3 — THE HOST OWNS THE NAVIGATION, so the book can be asked
   * not to draw a strip of its own. The Homes tab's section strip already names
   * `Accounts / Destinations` beside `Homes` (board 2812 M4 fix round: Agreed prices
   * left for a home's own Prices tab), and a second strip
   * inside it would be the same four choices twice.
   */
  hideStrip?: boolean;
  /**
   * ⭐ T1749 M7 (R5) — WHICH TAB OPENS. The shelf's `Customer accounts & prices`
   * entry lands on the Accounts strip, so the host says so rather than the book
   * guessing. Defaults to `accounts`, which is what it always opened on.
   */
  initialTab?: Tab;
  /** Refetch everything the screen shows. Threaded to all three children, and
   *  it doubles as the retry for the failure banner below. */
  onChanged: () => void;
};

type Tab = 'accounts' | 'destinations';

const TAB_LABELS: Readonly<Record<Tab, string>> = {
  accounts: 'Accounts',
  destinations: 'Destinations',
  /* ⭐ board 2812 M4 fix round (L8, plan-alignment L-2): `Agreed prices` LEFT this book.
     q747's reversal puts a home's prices on its own Prices tab, and two places for one
     price is the drift the reversal removes; Set up homes and places holds Accounts,
     Destinations and the roster. */
};

const TAB_ORDER: readonly Tab[] = ['accounts', 'destinations'];

export function AccountBook({
  accounts,
  destinations,
  billingParties,
  truncated,
  failed,
  loaded,
  initialTab = 'accounts',
  hideStrip,
  onChanged,
}: AccountBookProps) {
  /* ⭐ T1749 M7 wave-3 — THE HOST'S CHOICE WINS WHEN THE HOST OWNS THE STRIP. On
     a host that hides the strip there is no way to change the tab from inside the
     book, so `initialTab` is the whole answer and holding a copy in state would
     freeze it on the first render. With its own strip the book keeps its state. */
  const [ownTab, setOwnTab] = useState<Tab>(initialTab);
  const tab = hideStrip === true ? initialTab : ownTab;
  const setTab = setOwnTab;

  // The accounts tab renders BOTH the account rows and their bill-to labels, so
  // a cut billing-party list is a cut on that tab too: an account whose payer
  // fell off the end silently loses its "Billed to ..." line, which reads as
  // "this one bills itself" rather than "this one is not fully loaded".
  const listIsCut =
    (tab === 'accounts' && (truncated.accounts || truncated.billingParties))
    || (tab === 'destinations' && truncated.destinations);

  /**
   * ⛔ THIS DELIBERATELY DOES **NOT** MIRROR `listIsCut`, AND HARMONISING THE TWO
   * BACK WOULD REOPEN THE DEFECT THIS FILE WAS EDITED TO CLOSE.
   *
   * The rule is: does this tab render anything sourced from the failed read?
   * Yes → say so. No → stay quiet.
   *
   * The PRICES tab renders THREE reads, not one — the price rows, plus the two
   * `<select>` pickers that PriceList builds from `accounts` and `destinations`
   * ("The two catalogues arrive as props for exactly this"). So a failed
   * catalogue read leaves a picker holding nothing but its placeholder, and
   * "there are no destinations" is exactly the lie this change exists to stop.
   *
   * ⚠️ WHY THE TRUNCATION KEYING GETS AWAY WITH NOT DOING THIS: a TRUNCATED
   * catalogue still offers most of its options, so a picker that is merely cut
   * still works. A FAILED one offers none. The two facts genuinely differ, so
   * the two keyings genuinely differ.
   */
  const listFailed =
    (tab === 'accounts' && (failed.accounts || failed.billingParties))
    || (tab === 'destinations' && failed.destinations);

  return (
    <>
      {/* ⭐ T1749 M7 wave-3: the heading and the Back control are gone with the
          `onBack` prop. This book is a section of a screen that has both. */}

      {/* ⭐ T1749 M4 — THE TAB GRAMMAR IS `Button`'s `tab` VARIANT NOW (DS-5
          drift 3), and this is a DECLARED pixel change on this screen. The strip
          used to spend the BUTTON LEVELS on itself — `btn--secondary` for the
          chosen tab, `btn--quiet` for the others — which says "this is the
          alternative action" about something that is not an action at all, and
          left this screen reading as though it held two competing secondaries.
          The sitting rules the portal's underline as the one tab look, so these
          tabs are now a white bar with a gold-700 underline under the chosen
          one. ⛔ `aria-pressed` IS UNCHANGED and still carries the chosen state:
          it is now what the sheet paints from, so the look and the announcement
          cannot disagree. Every pin on this strip reads role and name. */}
      {/* ⛔ THE STRIP IS A NAMED GROUP (T1749 M4 fold, ux-review (a)). Its twin on
          Nursing homes has been `role="group"` with a descriptive name since it
          was written; this one was a bare `<div>`, so a screen-reader user heard
          three unrelated toggle buttons with nothing saying what they switch
          between. Same pattern, same voice, and it costs no pixel: a group is not
          a rendered box. ⚠️ The NAME is not a locked e2e name — the suite queries
          these by `getByRole('button')` — but it is read aloud, so it is written
          in the operator's words rather than in the component's. */}
      {/* ⭐⭐ T1749 M7 (ruling 7) — `.tab-strip`, NOT `.book-list-head`. The strip
          wore the kiosk's HEADING-ROW class, which is a `flex` with a `--space-4`
          gap and no band at all, so the shared white tab sat on page cream at a
          1.06:1 edge. The container is one shared rule now
          (`@ecmb/ui/styles/components.css`), and the heading-row class keeps its
          six real heading sites untouched: a strip is not a heading row, and
          making `.book-list-head` the band would have painted a band around
          every `<h1>` on this tool. */}
      {hideStrip !== true && (
        <div class="tab-strip" role="group" aria-label="Which part of the account book to show">
          {TAB_ORDER.map((t) => (
            <Button key={t} variant="tab" ariaPressed={t === tab} onClick={() => setTab(t)}>
              {TAB_LABELS[t]}
            </Button>
          ))}
        </div>
      )}

      {/* The FIRST load only: `loaded` never returns to false, so a refetch
          never comes back here. That is deliberate — blanking a list that is
          already on screen, to say "getting", would be a downgrade.
          ⚠️ STATED EXACTLY, BECAUSE THE LOOSE VERSION OF THIS SENTENCE IS FALSE:
          what survives a refetch is the rows of a read that SUCCEEDS. A refetch
          that FAILS replaces them with an empty list, because `setBook` writes a
          whole new snapshot rather than merging with the last one. This line is
          not claiming otherwise — the failure banner below is what covers that
          case. (Pre-existing either way: the `emptyList()` catch this replaced
          wiped the rows too.) */}
      {!loaded && <p class="helper-note">Getting the customer accounts…</p>}

      {/* ⛔ ONE STRING PER TAB, AND IT MUST BE TRUE IN EVERY STATE THAT RAISES IT
          ON THAT TAB. On the prices tab this banner has THREE causes (the price
          rows, the accounts picker, the destinations picker), so it must not
          assert anything about the price rows specifically. Borrowing the
          truncation banner's "This list is ..." framing one block down would, in
          the accounts-failed-on-prices-tab case, commit the exact error that
          banner's own note warns against: telling her rows are missing from a
          list that is complete. So the subject is the SCREEN, not the list.
          ⭐ "Nothing has been deleted" is carried over deliberately. An operator
          looking at an empty list after an outage, who concludes the rows are
          gone, is one step from re-adding what is already there — which splits
          one place into several rate keys (docs/pricing-spec.md).
          ⛔ ERROR BEFORE WARN, decided here rather than discovered later: both
          can be true at once on the prices tab, and the retryable failure is the
          one she should read first. */}
      {loaded && listFailed && (
        <Banner kind="error">
          Some of this could not be loaded, so what is on screen may be incomplete.
          Nothing has been deleted.{' '}
          <button type="button" class="btn btn--quiet btn--inline" onClick={onChanged}>
            Try again
          </button>
        </Banner>
      )}

      {/* ⛔ THE BOUND SAYING IT BIT. Not a page control and not a "load more"
          button: this book is deliberately un-paginated, and the requirement is
          only that a cut list stops looking like a complete one. `warn`, not
          `error` - nothing failed, and calling a working screen broken is its
          own kind of wrong. */}
      {listIsCut && (
        <Banner kind="warn">
          This list is longer than the screen can show, so some rows are not on it.
          Nothing has been deleted.
        </Banner>
      )}

      {/* ⛔ `emptinessUnknown` IS KEYED TO THE LIST EACH CHILD'S EMPTY SENTENCE IS
          ABOUT, which is NOT always what raised the banner above. AccountList
          gets `failed.accounts` alone: a failed billing-parties read makes the
          accounts tab incomplete (hence the banner) but it does not make the
          ACCOUNTS list's emptiness a lie, and suppressing the honest "no
          customer accounts yet" sentence on that basis would trade one wrong
          sentence for another. */}
      {tab === 'accounts' && (
        <AccountList
          accounts={accounts}
          billingParties={billingParties}
          emptinessUnknown={!loaded || failed.accounts}
          onChanged={onChanged}
        />
      )}
      {tab === 'destinations' && (
        <DestinationList
          destinations={destinations}
          emptinessUnknown={!loaded || failed.destinations}
          onChanged={onChanged}
        />
      )}
    </>
  );
}
